import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  GoogleMap,
  Marker,
  OverlayView,
  Polygon,
  Polyline,
} from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { useGoogleMaps } from '../../components/GoogleMapsProvider';
import Truck from '../../assets/images/Icon.png';
import { CustomParagraph } from '../../components';
import AirportIcon from '../../assets/images/airport.png';
import { getMapStyles } from '@/common/GoogleMapStyle.js';
import type { RootState } from '../../redux-toolkit/store';
import type { LatLng, ShipmentLocationData } from '@/apis/types';
import {
  downsampleEvenly,
  getPrimarySensorPoints,
  hasValidSensorCoordinates,
  MAX_SENSOR_MARKERS,
  MAX_SENSOR_POLYLINE_POINTS,
} from './mapSensorUtils';

interface Location {
  name?: string;
}

interface TrackingPoint {
  latitude: number;
  longitude: number;
  lat?: number;
  lng?: number;
  sensor_tracking_id?: string | null;
  sensor_priority?: number | null;
  [key: string]: unknown;
}

type TrackingPointWithLatLng = TrackingPoint & LatLng;

interface ShipmentPolylineSegment {
  polyline: TrackingPoint[];
  mode: string;
  status: string;
}

interface ShipmentData {
  shipment_monitor?: TrackingPoint[];
  shipment_polyline?: ShipmentPolylineSegment[];
  sensor?: {
    id?: string;
    sensor_tracking_id?: string;
  };
  sensors?: Array<{
    sensor_tracking_id?: string;
    priority?: number;
  }>;
  from_location?: ShipmentLocationData;
  to_location?: ShipmentLocationData;
}

interface ShipmentPreviewMapProps {
  onPointClicked: (point: TrackingPoint) => void;
  height?: string;
  fromLocation?: Location;
  fromLatLng?: LatLng;
  toLocation?: Location;
  toLatLng?: LatLng;
  shipment?: ShipmentData | null;
  lastCoordinates?: LatLng | null;
  isLargeScreen?: boolean;
  currentView?: 'summary' | 'detail';
}

/**
 * ShipmentPreviewMap - Displays an interactive Google Map preview showing shipment route and tracking points
 *
 * @param onPointClicked - Callback when a tracking point marker is clicked
 * @param height - Optional height for the map container (default: '320px')
 * @param fromLocation - Origin location with name
 * @param fromLatLng - Origin coordinates
 * @param toLocation - Destination location with name
 * @param toLatLng - Destination coordinates
 * @param shipment - Shipment data including monitoring points and polylines
 * @param lastCoordinates - Current position of the shipment (truck icon)
 * @param isLargeScreen - Whether to show full map controls and polygons
 * @param currentView - Map view mode: 'summary' shows courier routes, 'detail' shows tracking points
 */
const ShipmentPreviewMap: React.FC<ShipmentPreviewMapProps> = ({
  onPointClicked,
  height,

  fromLocation,
  fromLatLng,
  toLocation,
  toLatLng,

  shipment,
  lastCoordinates,
  isLargeScreen,
  currentView,
}) => {
  const { isLoaded } = useGoogleMaps();
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );
  const mapStyles = useMemo(
    () => getMapStyles(darkModeEnabled),
    [darkModeEnabled]
  );
  const [points, setPoints] = useState<TrackingPoint[]>([]);
  const [fromCoordinates, setFromCoordinates] = useState<LatLng | null>(null);
  const [toCoordinates, setToCoordinates] = useState<LatLng | null>(null);
  const [fromPlaceName, setFromPlaceName] = useState<string>('');
  const [toPlaceName, setToPlaceName] = useState<string>('');
  const [smallMapPath] = useState<LatLng[]>([]);
  const [showSmallPolyline] = useState<boolean>(false);

  const [sourcePopupPosition, setSourcePopupPosition] = useState<LatLng | null>(
    null
  );
  const [destinationPopupPosition, setDestinationPopupPosition] =
    useState<LatLng | null>(null);

  const [sourcePopupContent, setSourcePopupContent] =
    useState<React.ReactNode>(null);
  const [destinationPopupContent, setDestinationPopupContent] =
    useState<React.ReactNode>(null);

  const [fromPolygon, setFromPolygon] = useState<LatLng[] | null>(null);
  const [toPolygon, setToPolygon] = useState<LatLng[] | null>(null);
  const sensorPolylinePoints = useMemo(
    () => downsampleEvenly(points, MAX_SENSOR_POLYLINE_POINTS),
    [points]
  );
  const sensorMarkerPoints = useMemo(
    () => downsampleEvenly(points, MAX_SENSOR_MARKERS),
    [points]
  );

  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (
      (fromLocation || fromLatLng?.lat != null) &&
      (toLocation || toLatLng?.lat != null)
    ) {
      setFromCoordinates(fromLatLng?.lat != null ? fromLatLng : null);
      setToCoordinates(toLatLng?.lat != null ? toLatLng : null);
      setPoints(shipment ? getPrimarySensorPoints(shipment) : []);
      setFromPlaceName(fromLocation?.name || 'unknown location');
      setToPlaceName(toLocation?.name || 'unknown location');

      setFromPolygon(shipment?.from_location?.polygon);
      setToPolygon(shipment?.to_location?.polygon);
    }
  }, [fromLatLng, fromLocation, shipment, toLatLng, toLocation]);

  useEffect(() => {
    if (fromCoordinates?.lat != null && fromCoordinates?.lng != null) {
      setSourcePopupPosition(fromCoordinates);
      setSourcePopupContent(
        <CustomParagraph
          ellipsis={true}
          text={fromPlaceName}
          fontSize='14px'
          fontWeight={600}
          marginBottom={0}
        />
      );
    }
  }, [fromCoordinates, fromPlaceName]);

  useEffect(() => {
    if (toCoordinates?.lat != null && toCoordinates?.lng != null) {
      setDestinationPopupPosition(toCoordinates);
      setDestinationPopupContent(
        <CustomParagraph
          ellipsis={true}
          text={toPlaceName}
          fontSize='14px'
          fontWeight={600}
          marginBottom={0}
        />
      );
    }
  }, [toCoordinates, toPlaceName]);

  const secondaryMarkerColor = darkModeEnabled ? '#F2F3F5' : '#4B4B4B';
  // Accent (route line, tracking dots, halo): green in dark mode so the
  // route reads clearly against the dark tiles; unchanged blue in light
  // mode.
  const accentColor = darkModeEnabled ? '#34D399' : '#266EF1';
  // Unchanged from the original light-mode design (dark-gray / white fill);
  // only dark mode swaps to solid green/orange.
  const originMarkerColor = darkModeEnabled ? '#22C55E' : '#4B4B4B';
  const destinationMarkerColor = darkModeEnabled ? '#F97316' : '#FFFFFF';

  const polylineOptions = {
    strokeColor: accentColor,
    strokeOpacity: 1.0,
    strokeWeight: 3,
    fillColor: accentColor,
    icons: [
      {
        icon: {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 2,
          strokeColor: accentColor,
          fillColor: accentColor,
        },
        offset: '50%',
        repeat: '100px',
      },
    ],
  };

  const polylinePath = {
    strokeColor: secondaryMarkerColor,
    strokeOpacity: 1,
    strokeWeight: 3,
    fillColor: secondaryMarkerColor,
    icons: null as google.maps.IconSequence[] | null,
  };

  const markerSize = {
    url: Truck,
    scaledSize: new window.google.maps.Size(40, 40),
  };

  const mapContainerStyle = {
    width: '100%',
    height: height || '320px',
  };

  const circleIcon = {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: accentColor,
    fillOpacity: 1,
    strokeColor: accentColor,
    strokeWeight: 2,
    scale: 4,
  };

  const blackMarker = {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: originMarkerColor,
    fillOpacity: 1,
    strokeColor: darkModeEnabled ? '#FFFFFF' : originMarkerColor,
    strokeWeight: 2,
    scale: 10,
  };

  const whiteMarker = {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: destinationMarkerColor,
    fillOpacity: 1,
    strokeColor: darkModeEnabled ? '#FFFFFF' : '#4B4B4B',
    strokeWeight: darkModeEnabled ? 2 : 3,
    scale: 10,
  };

  const getCurrentPoint = (item: TrackingPoint) => {
    onPointClicked(item);
  };

  useEffect(() => {
    if (isLoaded && fromCoordinates && toCoordinates) {
      const bounds = new window.google.maps.LatLngBounds();
      // Extend bounds to include fromCoordinates and toCoordinates
      if (fromCoordinates?.lat != null && fromCoordinates?.lng != null) {
        bounds.extend(fromCoordinates);
      }

      if (toCoordinates?.lat != null && toCoordinates?.lng != null) {
        bounds.extend(toCoordinates);
      }

      // Include any other points, if applicable
      points.forEach((point) => {
        bounds.extend({ lat: point.latitude, lng: point.longitude });
      });
      // Fit the map to the calculated bounds
      if (mapRef.current) {
        mapRef.current.fitBounds(bounds);
      }
    }
  }, [isLoaded, fromCoordinates, toCoordinates, points]);

  //All code relative to summary view
  const [currentMapView, setCurrentMapView] = useState<'summary' | 'detail'>(
    'summary'
  );
  const [courierTrack, setCourierTrack] = useState<ShipmentPolylineSegment[]>(
    []
  );

  useEffect(() => {
    setCurrentMapView(currentView ? currentView : 'summary');
  }, [currentView]);

  useEffect(() => {
    if (currentView === 'summary') {
      if (
        shipment &&
        shipment?.shipment_polyline &&
        shipment?.shipment_polyline?.length > 0
      ) {
        setCourierTrack(
          shipment?.shipment_polyline.filter(
            (item) =>
              item?.polyline &&
              item?.polyline?.length > 0 &&
              item.status === 'travelled'
          )
        );
      }
    } else {
      setCourierTrack([]);
    }
  }, [shipment, currentMapView, currentView]);

  const airMarker = {
    url: AirportIcon,
    scaledSize: new window.google.maps.Size(30, 30),
  };

  return (
    <>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          options={{
            disableDefaultUI: !isLargeScreen, // Disables map controls if not large screen
            zoomControl: isLargeScreen, // Only show zoom control on large screens
            streetViewControl: isLargeScreen, // Only show street view control on large screens
            mapTypeControl: isLargeScreen, // Only show map type control on large screens
            styles: mapStyles,
          }}
          onLoad={(map) => {
            mapRef.current = map;
          }} // Set the map instance
        >
          {fromCoordinates && (
            <>
              {isLargeScreen ? (
                <>
                  {fromPolygon ? (
                    <Polygon
                      paths={fromPolygon}
                      options={{
                        fillColor: '#FF0000',
                        fillOpacity: 0.35,
                        strokeColor: '#FF0000',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                      }}
                    />
                  ) : null}

                  <Marker position={fromCoordinates} icon={blackMarker} />
                </>
              ) : (
                <Marker position={fromCoordinates} icon={blackMarker} />
              )}
            </>
          )}

          {toCoordinates && (
            <>
              {isLargeScreen ? (
                <>
                  {toPolygon ? (
                    <Polygon
                      paths={toPolygon}
                      options={{
                        fillColor: '#FF0000',
                        fillOpacity: 0.35,
                        strokeColor: '#FF0000',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                      }}
                    />
                  ) : null}

                  <Marker position={toCoordinates} icon={whiteMarker} />
                </>
              ) : (
                <Marker position={toCoordinates} icon={whiteMarker} />
              )}
            </>
          )}

          {sourcePopupPosition && (
            <OverlayView
              position={sourcePopupPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                className='custom-popup-info-window'
                style={
                  {
                    '--popup-accent-color': originMarkerColor,
                  } as React.CSSProperties
                }
              >
                {sourcePopupContent}
              </div>
            </OverlayView>
          )}

          {destinationPopupPosition && (
            <OverlayView
              position={destinationPopupPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                className='custom-popup-info-window destination-popup-window'
                style={
                  {
                    '--popup-accent-color': destinationMarkerColor,
                  } as React.CSSProperties
                }
              >
                {destinationPopupContent}
              </div>
            </OverlayView>
          )}

          {currentMapView === 'detail' ? (
            <>
              {lastCoordinates && (
                <Marker position={lastCoordinates} icon={markerSize} />
              )}

              {showSmallPolyline && smallMapPath.length > 0 && (
                <Polyline path={smallMapPath} options={polylinePath} />
              )}

              {sensorPolylinePoints.length > 1 && (
                <Polyline
                  path={sensorPolylinePoints.map((point) => ({
                    lat: point.latitude,
                    lng: point.longitude,
                  }))}
                  options={polylineOptions}
                />
              )}

              {sensorMarkerPoints.length > 0 &&
                sensorMarkerPoints.map((location, index) => (
                  <React.Fragment key={index}>
                    <Marker
                      onClick={() => getCurrentPoint(location)}
                      position={{
                        lat: location.latitude,
                        lng: location.longitude,
                      }}
                      icon={circleIcon}
                    />
                  </React.Fragment>
                ))}
            </>
          ) : (
            <>
              {courierTrack?.length > 0 &&
                courierTrack.map((element, index) => {
                  let courierPath: TrackingPointWithLatLng[] = [];
                  if (element?.polyline && element?.polyline.length > 0) {
                    courierPath = element.polyline
                      .filter((point) => hasValidSensorCoordinates(point))
                      .map((point) => ({
                        lat: point.latitude,
                        lng: point.longitude,
                        ...point,
                      })); // Filter out invalid points
                  }

                  const mode = element.mode;
                  return (
                    <React.Fragment key={index}>
                      {/* Render Polyline */}
                      <Polyline
                        path={courierPath}
                        options={
                          mode === 'air' ? polylinePath : polylineOptions
                        }
                      />

                      {/* Render Marker for each valid point */}
                      {courierPath.map((point, idx) => (
                        <Marker
                          key={idx}
                          position={{ lat: point.lat, lng: point.lng }}
                          icon={mode === 'air' ? airMarker : circleIcon} // Custom marker icon if required
                          onClick={() => getCurrentPoint(point)} // Callback on marker click
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
            </>
          )}
        </GoogleMap>
      ) : (
        <h1>Loading...</h1>
      )}
    </>
  );
};

export default ShipmentPreviewMap;
